import React, { useState, useEffect, useRef } from 'react';
import { Box, Progress, Text, VStack, Alert, AlertIcon, AlertTitle, AlertDescription, Heading, Spinner, Button, HStack, SimpleGrid, Image, Code, FormControl, FormLabel, Input, Divider, Link, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton, ModalFooter, Flex, IconButton, Center, useDisclosure } from '@chakra-ui/react';
import { useToast } from '@chakra-ui/react';
import supabase from '../utils/supabaseUtils';
import { CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { exportTemplate, loadTemplate, getAllBlocksInScene } from '../utils/cesdkUtils';
import { addWatermark, addLogoWatermark } from '../utils/imageUtils';
import { v4 as uuidv4 } from 'uuid';
import { FaEye } from 'react-icons/fa';

// Implementation that can work with or without a direct CESDK instance
const AutoTemplateGenerator = ({ approvalId, onClose, cesdk }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState('Initializing...');
  const [primaryImage, setPrimaryImage] = useState(null);
  const [approvedImages, setApprovedImages] = useState([]);
  const [userData, setUserData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [generatedTemplates, setGeneratedTemplates] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);
  const [customizationStep, setCustomizationStep] = useState(false);
  const [formData, setFormData] = useState({});
  const [allPlaceholders, setAllPlaceholders] = useState([]);
  const [cesdkAvailable, setCesdkAvailable] = useState(false);
  const toast = useToast();
  
  // For template modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  
  // Store reference to CESDK instance if provided
  const cesdkRef = useRef(cesdk);

  const [approvalDataState, setApprovalDataState] = useState(null)

  // Check if CESDK is available
  useEffect(() => {
    if (cesdk) {
      console.log("CESDK instance is available:", cesdk);
      cesdkRef.current = cesdk;
      setCesdkAvailable(true);
    } else {
      console.warn("No CESDK instance provided to AutoTemplateGenerator");
      setCesdkAvailable(false);
    }
  }, [cesdk]);

  // Handle input change for customization form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // This is after approvals are made and system collects placeholder data Modify handleCustomizationSubmit to properly handle user inputs
  const handleCustomizationSubmit = async (e) => {
    if (e) e.preventDefault();
    
    console.log("AutoTemplateGenerator: Submitting customization form with user values");
    console.log(`Current approvedImages state has ${approvedImages?.length || 0} images`);
    setLoading(true);
    setProgress('Generating templates with your customizations...');
    
    try {
      console.log(`Calling generateTemplates with ${approvedImages?.length || 0} images from state`);
      await generateTemplates(approvedImages);
    } catch (err) {
      console.error('Error generating templates:', err);
      setError("Failed to generate templates: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to open modal with selected image templates
  const openTemplateModal = (imageIndex) => {
    setSelectedImageIndex(imageIndex);
    setCurrentTemplateIndex(0);
    setIsModalOpen(true);
  };

  // Function to navigate to next template in modal
  const goToNextTemplate = () => {
    const templatesForThisImage = getTemplatesForImage(selectedImageIndex);
    setCurrentTemplateIndex((prevIndex) => 
      prevIndex === templatesForThisImage.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Function to navigate to previous template in modal
  const goToPrevTemplate = () => {
    const templatesForThisImage = getTemplatesForImage(selectedImageIndex);
    setCurrentTemplateIndex((prevIndex) => 
      prevIndex === 0 ? templatesForThisImage.length - 1 : prevIndex - 1
    );
  };

  // Function to get templates for a specific image
  const getTemplatesForImage = (imageIndex) => {
    if (imageIndex === null) return [];
    
    const currentImage = approvedImages[imageIndex];
    return generatedTemplates.filter(
      template => template.fromImage === imageIndex || 
                 template.imageUrl === (currentImage.image_url || currentImage.file_url)
    );
  };


  // JB: THis is the ACTUAL process that creates the final templates. Includes the const generateTemplates
  useEffect(() => {
    const processApproval = async () => {
      try {
        setLoading(true);
        setProgress('Initializing...');
        
        // Check if CESDK is available and log status
        if (cesdkRef.current) {
          console.log("Using CESDK instance for template generation");
        } else {
          console.warn("No CESDK instance available - templates will use fallback mode");
          
          // Try to access global CESDK if available
          if (window.CreativeEditorSDK) {
            console.log("Found global CreativeEditorSDK - attempting to use it");
            cesdkRef.current = window.CreativeEditorSDK;
            setCesdkAvailable(true);
          }
        }
        
        // Step 1: Fetch approval data
        let approvalData;
        try {
          approvalData = await fetchApprovalData(approvalId);
          if (!approvalData) {
            throw new Error("No approval data found for ID: " + approvalId);
          }
          setApprovalDataState(approvalData);
        } catch (approvalError) {
          console.error("Failed to fetch approval data:", approvalError);
          setError("Failed to fetch approval data: " + approvalError.message);
          return;
        }
        
        // Step 2: Fetch user data based on approval
        setProgress('Fetching user data...');
        let userData;
        try {
          userData = await fetchUserData(approvalData.user_id, approvalData.event_id);
          setUserData(userData);
        } catch (userError) {
          console.error("Failed to fetch user data:", userError);
          // Continue with minimal user data
          userData = {
            id: approvalData.user_id,
            name: "User " + approvalData.user_id.substring(0, 6),
            event: { id: approvalData.event_id }
          };
          setUserData(userData);
        }
        
        // Step 3: Get approved images
        setProgress('Identifying images...');
        try {
          // Get all approved images
          const approvedImageList = await fetchApprovedImages(approvalData.user_id, approvalId);
          
          if (!approvedImageList || approvedImageList.length === 0) {
            console.warn('No approved images found, cannot continue without images');
            throw new Error('No approved images found for user');
          }
          
          // Store all approved images
          console.log(`Setting approvedImages state with ${approvedImageList.length} images`);
          setApprovedImages(approvedImageList);
          console.log(`Found ${approvedImageList.length} approved images`);
          
          // Also set the primary image for backward compatibility
          if (approvalData.primary_image_id) {
            // If we have a primary image ID in the approval, find it in the approved images
            const primary = approvedImageList.find(img => img.id === approvalData.primary_image_id);
            if (primary) {
              const primaryUrl = primary.image_url || primary.file_url;
              setPrimaryImage(primaryUrl);
              console.log("Set primary image from approval data:", primaryUrl);
            } else {
              // If the primary image isn't in approved images, use the first one
              const primaryUrl = approvedImageList[0].image_url || approvedImageList[0].file_url;
              setPrimaryImage(primaryUrl);
              console.log("Primary image not found in approved images, using first image:", primaryUrl);
            }
          } else {
            // Use the first approved image as primary if none specified
            const primaryUrl = approvedImageList[0].image_url || approvedImageList[0].file_url;
            setPrimaryImage(primaryUrl);
            console.log("No primary image specified, using first approved image:", primaryUrl);
          }
        } catch (imageError) {
          console.error('Error fetching images:', imageError);
          throw new Error('Images are required: ' + imageError.message);
        }
        
        // Step 4: Fetch templates for the event
        setProgress('Fetching templates...');
        let templates;
        try {
          templates = await fetchTemplates(approvalData.event_id);
          setTemplates(templates);
          
          // Extract all unique placeholders from templates for customization form
          const allPlaceholders = [];
          const placeholderNames = new Set();
          
          console.log(`Processing ${templates.length} templates for placeholders`);
          
          templates.forEach((template, index) => {
            console.log(`Analyzing template ${index + 1}/${templates.length}: ${template.name} (ID: ${template.id})`);
            let placeholderData = template.placeholder_data;
            
            // Try to parse placeholder_data if it's a string
            if (typeof placeholderData === 'string') {
              try {
                placeholderData = JSON.parse(placeholderData);
                console.log(`Successfully parsed placeholder_data for template: ${template.name}`);
              } catch (error) {
                console.error(`Error parsing placeholder_data string for template ${template.name}:`, error);
                placeholderData = [];
              }
            } else if (!placeholderData) {
              console.log(`No placeholder data found for template: ${template.name}`);
              placeholderData = [];
            } else if (!Array.isArray(placeholderData)) {
              console.log(`Placeholder data for template ${template.name} is not an array, converting:`, placeholderData);
              placeholderData = [];
            }
            
            if (placeholderData && Array.isArray(placeholderData)) {
              console.log(`Template ${template.name} has ${placeholderData.length} placeholders`);
              placeholderData.forEach(placeholder => {
                // Only add unique text placeholders
                if (placeholder.type === '//ly.img.ubq/text' && !placeholderNames.has(placeholder.name)) {
                  placeholderNames.add(placeholder.name);
                  allPlaceholders.push(placeholder);
                  console.log(`Added text placeholder: ${placeholder.name}`);
                } else if (placeholder.type === '//ly.img.ubq/graphic') {
                  console.log(`Found image placeholder: ${placeholder.name}`);
                }
              });
            }
          });
          
          console.log(`Found a total of ${allPlaceholders.length} unique text placeholders`);
          setAllPlaceholders(allPlaceholders);
          
          // Always initialize formData regardless of whether there are placeholders
          const initialFormData = {};
          
          // Add common default fields that might be needed even if no placeholders were found
          const userName = userData.name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
          initialFormData["Name"] = userName;
          initialFormData["name"] = userName;
          initialFormData["Player"] = userName;
          initialFormData["player"] = userName;
          initialFormData["Number"] = userData.jersey_number || '00';
          initialFormData["number"] = userData.jersey_number || '00';
          
          // Add data for any found placeholders
          allPlaceholders.forEach(placeholder => {
            if (placeholder.name.toLowerCase().includes('name')) {
              initialFormData[placeholder.name] = userName;
            } 
            else if (placeholder.name.toLowerCase().includes('number') || 
                     placeholder.name.toLowerCase().includes('#')) {
              initialFormData[placeholder.name] = userData.jersey_number || '00';
            }
            else {
              initialFormData[placeholder.name] = placeholder.defaultValue || '';
            }
          });
          
          console.log("Form data initialized:", initialFormData);
          setFormData(initialFormData);
          
          // Move to customization step if we have placeholders
          if (allPlaceholders.length > 0) {
            console.log("Found placeholders, showing customization form:", allPlaceholders);
            setCustomizationStep(true);
            setLoading(false);
          } else {
            // If no customizable placeholders, proceed directly to generation
            console.log(`No placeholders found, proceeding directly to template generation with ${approvedImageList.length} images`);
            await generateTemplates(approvedImageList);
          }
          
        } catch (templatesError) {
          console.error("Failed to fetch templates:", templatesError);
          setError("Failed to fetch templates: " + templatesError.message);
          return;
        }
      } catch (err) {
        console.error('Error in template setup:', err);
        setError("Failed during setup: " + err.message);
        setDebugInfo(JSON.stringify({
          error: err.message,
          stack: err.stack,
          name: err.name
        }, null, 2));
        toast({
          title: "Setup failed",
          description: err.message || 'Failed to setup template generation',
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        setLoading(false);
      }
    };
    
    if (approvalId) {
      processApproval();
    }
  }, [approvalId, toast]);

  // Core template generation function
  const generateTemplates = async (imagesToUse = null) => {
    try {
      setLoading(true);
      setProgress('Processing templates...');
      
      // Use passed-in images or fall back to approvedImages state
      const imagesToProcess = imagesToUse || approvedImages;
      
      // Add better diagnostic logging
      console.log(`generateTemplates: Starting with ${imagesToProcess ? imagesToProcess.length : 0} images (direct pass: ${!!imagesToUse}, state: ${approvedImages ? approvedImages.length : 0} images)`);
      
      // Ensure we have images to work with
      if (!imagesToProcess || imagesToProcess.length === 0) {
        console.error('generateTemplates: No images available', { 
          directlyPassedImages: imagesToUse ? imagesToUse.length : null,
          approvedImagesState: approvedImages ? approvedImages.length : null 
        });
        throw new Error('No approved images found for template generation');
      }
      
      // Log image details for debugging
      console.log('generateTemplates: Using images:', imagesToProcess.map(img => ({
        id: img.id,
        url: img.image_url || img.file_url,
        is_primary: img.is_primary
      })));
      
      // Track total templates to generate
      const totalToGenerate = templates.length * imagesToProcess.length;
      let processedCount = 0;
      
      // Use all approved images to generate templates
      const results = [];
      
      // For each approved image
      for (let imgIndex = 0; imgIndex < imagesToProcess.length; imgIndex++) {
        const currentImage = imagesToProcess[imgIndex];
        const imageUrl = currentImage.image_url || currentImage.file_url;
        
        setProgress(`Processing image ${imgIndex + 1} of ${imagesToProcess.length}...`);
        console.log(`Processing image ${imgIndex + 1}/${imagesToProcess.length}: ${imageUrl}`);
        
        // Process each template for this image
        for (let i = 0; i < templates.length; i++) {
          const template = templates[i];
          processedCount++;
          setProgress(`Processing template ${i + 1} of ${templates.length} for image ${imgIndex + 1}/${imagesToProcess.length} (${Math.round((processedCount/totalToGenerate)*100)}%)...`);
          
          try {
            console.log(`Template ${template.name} placeholders:`, template.placeholder_data);
            
            // Create replacement data based on template placeholders and form data
            const replacementData = { ...formData };
            
            // Process placeholder data if available
            let placeholderData = template.placeholder_data;
            let hasProcessedPlaceholders = false;
            
            // Try to parse placeholder_data if it's a string
            if (typeof placeholderData === 'string') {
              try {
                placeholderData = JSON.parse(placeholderData);
                hasProcessedPlaceholders = true;
              } catch (error) {
                console.error(`Error parsing placeholder_data string for template ${template.name}:`, error);
                placeholderData = [];
              }
            }
            
            if (placeholderData && Array.isArray(placeholderData) && placeholderData.length > 0) {
              console.log(`Processing ${placeholderData.length} placeholders for template ${template.name}`);
              
              placeholderData.forEach(placeholder => {
                console.log(`Processing placeholder: ${placeholder.name}, type: ${placeholder.type}`);
                
                // Handle image placeholders (specifically look for "Person" or any image placeholder)
                if (placeholder.type === '//ly.img.ubq/graphic' || 
                    placeholder.name.toLowerCase() === 'person' || 
                    placeholder.name.toLowerCase() === 'source') {
                  replacementData[placeholder.name] = imageUrl; // Use the current image from the loop
                  hasProcessedPlaceholders = true;
                } 
                // Text placeholders are already in formData from the customization step
              });
            } 
            
            // Always add default image mappings, whether or not we processed placeholders
            console.log(`${hasProcessedPlaceholders ? "Adding additional" : "No placeholder data found, using"} generic mappings for images`);
            
            // Default mappings for common placeholders - use the current image
            replacementData["Person"] = imageUrl;
            replacementData["person"] = imageUrl;
            replacementData["Source"] = imageUrl;
            replacementData["source"] = imageUrl;
            replacementData["Image"] = imageUrl;
            replacementData["image"] = imageUrl;
            replacementData["Athlete"] = imageUrl;
            replacementData["athlete"] = imageUrl;
            replacementData["Player"] = imageUrl;
            replacementData["player"] = imageUrl;
            
            console.log("Final replacement data:", replacementData);
            
            let exportUrl = null;
            let localObjectUrl = null;
            
            // CESDK Approach: If CESDK is available, use it
            if (cesdkRef.current) {
              console.log(`Exporting template ${template.name} with ID: ${template.id} using CESDK`);
              try {
                // Make sure CESDK is initialized and ready
                if (typeof cesdkRef.current.editor !== 'undefined') {
                  console.log("CESDK editor is available");
                }
                
                /**
                 * Custom template export function that properly handles image proportions
                 * 
                 * KEY IMPLEMENTATION NOTES:
                 * 1. Image Scaling Fix:
                 *    - Uses createFill("image") instead of createFill('//ly.img.ubq/fill/image')
                 *    - Sets square dimensions (1080x1080) to prevent distortion
                 *    - Explicitly sets "fit" mode to maintain aspect ratio
                 *    - Centers image using position x/y = 0.5
                 * 
                 * 2. Compatibility:
                 *    - Supports both setSourceSet (newer CESDK) and setString (older CESDK)
                 *    - Uses consistent approach for both new and existing fills
                 *    - Includes proper error handling and fallbacks
                 * 
                 * 3. Best Practices:
                 *    - Sets fill on block before modifying fill properties
                 *    - Locks aspect ratio of the container
                 *    - Uses minimal delay (50ms) for fill creation to take effect
                 * 
                 * This approach matches the working implementation in NewDashboard.jsx
                 * which correctly displays primary images with proper proportions.
                 */
                const customExportTemplate = async (cesdk, templateId, replacementData) => {
                  try {
                    console.log("Starting custom export process for template:", templateId);
                    console.log("Replacement data:", replacementData);
                    
                    // Load the template
                    let loaded = false;
                    try {
                      loaded = await loadTemplate(cesdk, templateId);
                      console.log("Template loaded successfully:", loaded);
                    } catch (loadError) {
                      console.error("Error loading template:", loadError);
                      throw new Error(`Failed to load template: ${loadError.message}`);
                    }
                    
                    if (!loaded) {
                      console.error("Template loading returned false");
                      throw new Error("Failed to load template");
                    }
                    
                    let scene;
                    try {
                      scene = cesdk.engine.scene.get();
                      console.log("Scene retrieved:", !!scene);
                    } catch (sceneError) {
                      console.error("Error getting scene:", sceneError);
                      throw new Error("Could not access scene");
                    }
                    
                    if (!scene) {
                      console.error("No scene was loaded after template load");
                      throw new Error("No active scene loaded");
                    }
                    
                    // Get all blocks in scene
                    const blocks = getAllBlocksInScene(cesdk, scene);
                    console.log("Found blocks in scene:", blocks.length);
                    
                    // Log all blocks with their names and types
                    console.log("Blocks in scene:", blocks.map(blockId => {
                      try {
                        return {
                          id: blockId,
                          name: cesdk.engine.block.getName(blockId),
                          type: cesdk.engine.block.getType(blockId),
                          hasFill: !!cesdk.engine.block.getFill(blockId)
                        };
                      } catch (e) {
                        return { id: blockId, error: e.message };
                      }
                    }));
                    
                    // Go through each replacement data item
                    for (const [key, value] of Object.entries(replacementData)) {
                      // Find block with this name
                      console.log(`Looking for block with name "${key}"`);
                      const blockId = blocks.find(b => {
                        try {
                          return cesdk.engine.block.getName(b) === key;
                        } catch (e) {
                          console.error(`Error getting name for block ${b}:`, e);
                          return false;
                        }
                      });
                      
                      if (blockId) {
                        console.log(`Found block with ID ${blockId} for "${key}"`);
                        
                        try {
                          const type = cesdk.engine.block.getType(blockId);
                          console.log(`Block type for "${key}": ${type}`);
                          
                          // Handle text blocks
                          if (type === '//ly.img.ubq/text') {
                            cesdk.engine.block.setString(blockId, "text/text", value);
                            console.log(`Set text for ${key} to "${value}"`);
                          } 
                          // Special handling for graphic blocks (images)
                          else if (type === '//ly.img.ubq/graphic' && value.startsWith('http')) {
                            console.log(`Setting image for ${key} (${blockId}) to ${value}`);
                            
                            try {
                              // First check if the block already has a fill
                              const existingFill = cesdk.engine.block.getFill(blockId);
                              console.log(`Existing fill for "${key}":`, existingFill);
                              
                              if (!existingFill) {
                                // Create a new fill using the same approach as NewDashboard.jsx
                                console.log(`Creating new image fill for ${key}`);
                                const fillId = cesdk.engine.block.createFill("image");
                                console.log(`Created new fill with ID: ${fillId}`);
                                
                                // Ensure fill is enabled
                                cesdk.engine.block.setFillEnabled(fillId, true);
                                
                                // Set the fill on the block first
                                cesdk.engine.block.setFill(blockId, fillId);
                                
                                // Wait for the fill to be properly attached
                                await new Promise(resolve => setTimeout(resolve, 50));
                                
                                // Now set the image source using both approaches to ensure compatibility
                                try {
                                  // First try sourceSet method (newer CESDK)
                                  cesdk.engine.block.setSourceSet(fillId, "fill/image/sourceSet", [
                                    {
                                      uri: value,
                                    },
                                  ]);
                                  console.log("Applied image with setSourceSet");
                                } catch (sourceSetError) {
                                  // Fall back to string method (older CESDK)
                                  console.log("setSourceSet failed, trying setString:", sourceSetError);
                                  cesdk.engine.block.setString(fillId, "fill/image/imageFileURI", value);
                                  console.log("Applied image with setString");
                                }
                                
                                // Apply settings that match NewDashboard.jsx approach
                                try {
                                  // Set to 'fit' mode explicitly
                                  cesdk.engine.block.setEnum(fillId, "fill/image/mode", "fit");
                                  console.log(`Set fill mode to "fit" for ${blockId}`);
                                  
                                  // Lock aspect ratio of the block
                                  cesdk.engine.block.setBool(blockId, "constraints/aspectRatio/locked", true);
                                  
                                  // Set position to center (for better centering of content)
                                  if (cesdk.engine.block.hasProperty(fillId, "fill/image/position/x")) {
                                    cesdk.engine.block.setDouble(fillId, "fill/image/position/x", 0.5);
                                    cesdk.engine.block.setDouble(fillId, "fill/image/position/y", 0.5);
                                  }
                                } catch (propError) {
                                  console.warn(`Error setting additional properties:`, propError);
                                }
                              } else {
                                // Use the existing fill with the same improved approach
                                console.log(`Using existing fill ${existingFill} for ${key}`);
                                
                                // Try both image setting methods for compatibility
                                try {
                                  // First try sourceSet (newer CESDK)
                                  cesdk.engine.block.setSourceSet(existingFill, "fill/image/sourceSet", [
                                    {
                                      uri: value,
                                    },
                                  ]);
                                } catch (sourceSetError) {
                                  // Fall back to string method (older CESDK)
                                  console.log("setSourceSet failed for existing fill, trying setString:", sourceSetError);
                                  cesdk.engine.block.setString(existingFill, "fill/image/imageFileURI", value);
                                }
                                
                                // Apply the same settings for consistent behavior
                                try {
                                  // Set to 'fit' mode explicitly
                                  cesdk.engine.block.setEnum(existingFill, "fill/image/mode", "fit");
                                  
                                  // Lock aspect ratio
                                  cesdk.engine.block.setBool(blockId, "constraints/aspectRatio/locked", true);
                                  
                                  // Center the image within the fill
                                  if (cesdk.engine.block.hasProperty(existingFill, "fill/image/position/x")) {
                                    cesdk.engine.block.setDouble(existingFill, "fill/image/position/x", 0.5);
                                    cesdk.engine.block.setDouble(existingFill, "fill/image/position/y", 0.5);
                                  }
                                } catch (propError) {
                                  console.warn(`Error setting additional properties on existing fill:`, propError);
                                }
                              }
                              console.log(`Successfully set image for ${key}`);
                            } catch (fillErr) {
                              console.error(`Error setting fill for ${key}:`, fillErr);
                              
                              // Try alternative approach - create a new graphic block with the image
                              try {
                                console.log("Trying alternative approach for image replacement");
                                
                                // Try to directly export the image without template replacement
                                console.log("Attempting to export the template as-is without image replacement");
                                const exportBlob = await cesdk.engine.block.export(scene, "image/png", {
                                  targetWidth: 1920,
                                  targetHeight: 1080,
                                  dpi: 72,
                                  exportArea: "design"
                                });
                                
                                // Create a local objectURL for browser download
                                const localObjectUrl = URL.createObjectURL(exportBlob);
                                console.log("Created fallback export without image replacement:", localObjectUrl);
                                
                                // Return with limited success
                                return { 
                                  supabaseUrl: null, 
                                  localObjectUrl, 
                                  fileName: `${templateId}-export-${Date.now()}.png`,
                                  fallback: true
                                };
                              } catch (altErr) {
                                console.error("Alternative approach also failed:", altErr);
                                throw new Error("Could not replace image or export template");
                              }
                            }
                          }
                        } catch (blockErr) {
                          console.error(`Error processing block for "${key}":`, blockErr);
                        }
                      } else {
                        console.warn(`No block found with name "${key}"`);
                      }
                    }
                    
                    // NEW CODE: Add a fallback approach for templates without proper placeholders
                    // Check if we've successfully processed any image blocks through placeholder names
                    const hasProcessedAnyImageBlocks = Object.keys(replacementData).some(key => {
                      const block = blocks.find(b => {
                        try {
                          return cesdk.engine.block.getName(b) === key;
                        } catch (e) {
                          return false;
                        }
                      });
                      
                      if (!block) return false;
                      
                      try {
                        return cesdk.engine.block.getType(block) === '//ly.img.ubq/graphic';
                      } catch (e) {
                        return false;
                      }
                    });
                    
                    // If we haven't matched any image blocks by name but have image URLs available,
                    // try to apply our image to any graphic blocks as a fallback
                    if (!hasProcessedAnyImageBlocks) {
                      console.log("No image blocks were matched by name, trying fallback approach");
                      
                      // Find image URLs in replacement data
                      const imageUrls = Object.values(replacementData)
                        .filter(v => typeof v === 'string' && v.startsWith('http'));
                      
                      if (imageUrls.length > 0) {
                        // Find graphic blocks
                        const graphicBlocks = blocks.filter(b => {
                          try {
                            return cesdk.engine.block.getType(b) === '//ly.img.ubq/graphic';
                          } catch (e) {
                            return false;
                          }
                        });
                        
                        console.log(`Found ${graphicBlocks.length} graphic blocks for fallback approach`);
                        
                        if (graphicBlocks.length > 0) {
                          // Use first graphic block with first image
                          const targetBlock = graphicBlocks[0];
                          const imageUrl = imageUrls[0];
                          
                          console.log(`Applying fallback image (${imageUrl}) to graphic block`);
                          
                          try {
                            // Check for existing fill
                            const existingFill = cesdk.engine.block.getFill(targetBlock);
                            
                            if (!existingFill) {
                              // Create new fill
                              const fillId = cesdk.engine.block.createFill("image");
                              cesdk.engine.block.setFillEnabled(fillId, true);
                              cesdk.engine.block.setFill(targetBlock, fillId);
                              
                              // Wait for fill attachment
                              await new Promise(resolve => setTimeout(resolve, 50));
                              
                              // Try both methods to set image source
                              try {
                                cesdk.engine.block.setSourceSet(fillId, "fill/image/sourceSet", [
                                  { uri: imageUrl }
                                ]);
                              } catch (e) {
                                cesdk.engine.block.setString(fillId, "fill/image/imageFileURI", imageUrl);
                              }
                              
                              // Set properties for best display
                              try {
                                cesdk.engine.block.setEnum(fillId, "fill/image/mode", "fit");
                                cesdk.engine.block.setBool(targetBlock, "constraints/aspectRatio/locked", true);
                                
                                if (cesdk.engine.block.hasProperty(fillId, "fill/image/position/x")) {
                                  cesdk.engine.block.setDouble(fillId, "fill/image/position/x", 0.5);
                                  cesdk.engine.block.setDouble(fillId, "fill/image/position/y", 0.5);
                                }
                              } catch (e) {
                                console.warn("Error setting fallback image properties:", e);
                              }
                            } else {
                              // Use existing fill
                              try {
                                cesdk.engine.block.setSourceSet(existingFill, "fill/image/sourceSet", [
                                  { uri: imageUrl }
                                ]);
                              } catch (e) {
                                cesdk.engine.block.setString(existingFill, "fill/image/imageFileURI", imageUrl);
                              }
                            }
                            
                            console.log("Successfully applied fallback image approach");
                          } catch (e) {
                            console.error("Error applying fallback image:", e);
                          }
                        }
                      }
                    }
                    
                    // Export the scene
                    try {
                      console.log("Attempting to export the scene to a Blob");
                      const exportBlob = await cesdk.engine.block.export(scene, "image/png", {
                        targetWidth: 1920,
                        targetHeight: 1080,
                        dpi: 72,
                        exportArea: "design"
                      });
                      
                      // Create a local objectURL for browser download (unwatermarked version)
                      const localObjectUrl = URL.createObjectURL(exportBlob);
                      console.log("Created local object URL:", localObjectUrl);
                      
                      // Create watermarked version for display with a stronger, cross-image watermark
                      console.log("Applying watermark to exported image...");
                      try {
                        const watermarkedBlob = await addWatermark(exportBlob, {
                          text: "PlayMaker Pics",
                          color: "rgba(255, 255, 255, 0.8)",
                          font: "bold 36px Arial",
                          repeating: true,
                          diagonal: true,
                        });
                        
                        console.log("Watermark applied successfully", watermarkedBlob instanceof Blob ? "blob created" : "failed to create blob");
                        
                        // Create URL for watermarked version
                        const watermarkedObjectUrl = URL.createObjectURL(watermarkedBlob);
                        console.log("Watermarked object URL created:", watermarkedObjectUrl);
                        
                        // Upload both versions to Supabase
                        const exportFileName = `${templateId}-export-${Date.now()}.png`;
                        const watermarkedFileName = `${templateId}-watermarked-${Date.now()}.png`;
                        
                        try {
                          // Upload original (non-watermarked) version
                          console.log("Uploading original file to Supabase:", exportFileName);
                          const { data, error: exportError } = await supabase.storage
                            .from('playmakerpics-images')
                            .upload(`exports/${userData.id}/${exportFileName}`, exportBlob, {
                              upsert: true,
                              cacheControl: 'no-cache',
                              contentType: 'image/png'
                            });
                          
                          if (exportError) {
                            console.error("Error uploading original file:", exportError);
                            throw exportError;
                          }
                          
                          console.log("Original upload success, data:", data);
                          
                          // Get URL for original unwatermarked version
                          const { data: urlData } = supabase.storage
                            .from('playmakerpics-images')
                            .getPublicUrl(`exports/${userData.id}/${exportFileName}`);
                            
                          const supabaseUrl = `${urlData.publicUrl}?t=${Date.now()}`;
                          console.log("Generated supabase URL:", supabaseUrl);
                              
                          // Upload watermarked version to a separate directory
                          console.log("Uploading watermarked file to Supabase:", watermarkedFileName);
                          const { data: wmData, error: wmError } = await supabase.storage
                            .from('playmakerpics-images')
                            .upload(`exports/${userData.id}/watermarked/${watermarkedFileName}`, watermarkedBlob, {
                              upsert: true,
                              cacheControl: 'no-cache',
                              contentType: 'image/png'
                            });
                              
                          console.log("Watermarked upload result:", wmData, wmError);
                              
                          // Get URL for watermarked version
                          let watermarkedUrl = null;
                          if (!wmError) {
                            const { data: wmUrlData } = supabase.storage
                              .from('playmakerpics-images')
                              .getPublicUrl(`exports/${userData.id}/watermarked/${watermarkedFileName}`);
                              
                            watermarkedUrl = `${wmUrlData.publicUrl}?t=${Date.now()}`;
                          }
                          
                          console.log("✅ Original export uploaded:", supabaseUrl);
                          console.log("✅ Watermarked version uploaded:", watermarkedUrl);
                          
                          // Return both URLs
                          return {
                            supabaseUrl,
                            watermarkedUrl,
                            localObjectUrl,
                            watermarkedObjectUrl,
                            fileName: exportFileName,
                            watermarkedFileName
                          };
                        } catch (uploadError) {
                          console.error("Failed to upload to Supabase:", uploadError);
                          
                          // Return local URLs if upload fails
                          return {
                            localObjectUrl,
                            watermarkedObjectUrl,
                            fileName: exportFileName,
                            watermarkedFileName
                          };
                        }
                      } catch (watermarkError) {
                        console.error("Failed to apply watermark:", watermarkError);
                        // Continue with unwatermarked version
                        const watermarkedBlob = exportBlob; // Fallback to original
                        const watermarkedObjectUrl = localObjectUrl;
                        console.log("Using unwatermarked version as fallback due to error");
                      }
                    } catch (exportError) {
                      console.error("Failed to export scene:", exportError);
                      throw new Error(`Failed to export template ${templateId}: ${exportError.message}`);
                    }
                  } catch (error) {
                    console.error("❌ Custom export failed:", error);
                    return null;
                  }
                };
                
                // Use our custom export function which already works
                const exportResult = await customExportTemplate(cesdkRef.current, template.id, replacementData);
                
                if (exportResult) {
                  exportUrl = exportResult.supabaseUrl || null;
                  const watermarkedUrl = exportResult.watermarkedUrl || null;
                  localObjectUrl = exportResult.localObjectUrl || exportResult.watermarkedObjectUrl || null;
                  console.log("Successfully exported template with CESDK:", exportUrl);
                  console.log("Watermarked version:", watermarkedUrl);
                  
                  // Add to the result with both URLs and ensure supabaseUrl takes priority
                  results.push({
                    id: template.id,
                    name: template.name,
                    url: exportUrl || localObjectUrl, // Use supabaseUrl if available, otherwise localObjectUrl
                    watermarkedUrl: watermarkedUrl, // Store watermarked URL
                    localUrl: localObjectUrl,
                    watermarkedLocalUrl: exportResult.watermarkedObjectUrl,
                    fileName: exportResult.fileName,
                    watermarkedFileName: exportResult.watermarkedFileName,
                    supabaseUrl: exportUrl, // Store the Supabase URL separately to ensure we use it for database
                    fromImage: imgIndex, // Track which image was used
                    imageUrl: imageUrl // Store reference to original image                  
                  });
                } else {
                  console.error(`Failed to export template ${template.name}`);
                  
                  // Still add to results with fallback to thumbnail
                  exportUrl = template.thumbnail_url || `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'><rect width='1920' height='1080' fill='%234299E1'/><text x='50%' y='50%' font-size='72' text-anchor='middle' fill='white'>${template.name} (CESDK Error)</text></svg>`;
                  
                  results.push({
                    id: template.id,
                    name: template.name,
                    url: exportUrl,
                    placeholder_values: formData,
                    fromImage: imgIndex, // Track which image was used
                    imageUrl: imageUrl // Store reference to original image
                  });
                }
              } catch (cesdkError) {
                console.error("Error using CESDK for export:", cesdkError);
                // Fall back to thumbnail if CESDK export fails
                exportUrl = template.thumbnail_url || `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'><rect width='1920' height='1080' fill='%234299E1'/><text x='50%' y='50%' font-size='72' text-anchor='middle' fill='white'>${template.name}</text></svg>`;
              }
            } 
            // Fallback Approach: When CESDK is not available
            else {
              console.log(`No CESDK available, using fallback for template ${template.name}`);
              
              // Use the template's thumbnail or create a SVG placeholder
              exportUrl = template.thumbnail_url || `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='1920' height='1080'><rect width='1920' height='1080' fill='%234299E1'/><text x='50%' y='50%' font-size='72' text-anchor='middle' fill='white'>${template.name}</text></svg>`;
              
              // Add to results
              results.push({
                id: template.id,
                name: template.name,
                url: exportUrl,
                fromImage: imgIndex, // Track which image was used
                imageUrl: imageUrl // Store reference to original image
              });
            }
            
            // Try to insert into exports table with minimal fields
            // Only save if we have valid URLs
            if (exportUrl || localObjectUrl) {
              try {
                const currentResult = results[results.length - 1];
                
                // Validate the export URL before saving to the database
                let finalExportUrl = currentResult.supabaseUrl || currentResult.url;
                let watermarkedExportUrl = currentResult.watermarkedUrl;
                
                // Don't save blob URLs to the database - they're temporary local URLs
                if (finalExportUrl && finalExportUrl.startsWith('blob:')) {
                  console.warn("Export URL is a blob URL which can't be stored in database:", finalExportUrl);
                  // Try to use thumbnail or other fallback
                  finalExportUrl = template.thumbnail_url || null;
                }
                
                if (watermarkedExportUrl && watermarkedExportUrl.startsWith('blob:')) {
                  console.warn("Watermarked URL is a blob URL which can't be stored in database:", watermarkedExportUrl);
                  watermarkedExportUrl = null;
                }
                
                // Check if we have a valid URL that contains .png
                if (finalExportUrl && !finalExportUrl.includes('.png')) {
                  console.warn("Export URL doesn't contain .png, this may cause issues:", finalExportUrl);
                  
                  // If it's a .ubq file, attempt to correct the path
                  if (finalExportUrl.includes('.ubq')) {
                    // Try to fix the URL by changing .ubq to .png and ensuring it's in the exports/userId folder
                    const correctedUrl = finalExportUrl
                      .replace('.ubq', '.png')
                      .replace('/templates/', `/exports/${userData.id}/`)
                      .replace('/exports/', `/exports/${userData.id}/`); 
                    
                    console.log("Attempting to correct URL from .ubq to .png:", correctedUrl);
                    finalExportUrl = correctedUrl;
                  }
                }
                
                // Create the export data with both URLs
                const exportData = {
                  user_id: userData.id,
                  template_id: template.id,
                  export_url: finalExportUrl,
                  export_url_wm: watermarkedExportUrl, // Store watermarked URL in new column
                  exported_at: new Date().toISOString(),
                  is_saved: false,

                  event_id: template.event_id || approvalDataState?.event_id || null,
                  source_upload_id: currentImage.id,
                  placeholder_values: replacementData  // jsonb
                };

                // Store source image reference in client-side only
                const sourceImageData = {
                  id: currentImage.id,
                  url: imageUrl
                };
                
                console.log("Inserting into exports table with URLs:", {
                  original: finalExportUrl,
                  watermarked: watermarkedExportUrl
                });
                
                const { data: insertedData, error: exportError } = await supabase
                  .from('exports')
                  .insert(exportData)
                  .select('id, export_url, export_url_wm');
                
                if (exportError) {
                  console.error("Could not save to exports table. Error details:", {
                    message: exportError.message,
                    details: exportError.details,
                    hint: exportError.hint,
                    code: exportError.code
                  });
                } else {
                  console.log("✅ Saved to exports table successfully:", insertedData);
                  
                  // Add the export_url to results for future reference
                  if (insertedData && insertedData.length > 0) {
                    // Update the url in results to maintain consistent data
                    const resultIndex = results.findIndex(r => 
                      r.id === template.id && 
                      r.fromImage === imgIndex && 
                      r.imageUrl === imageUrl);
                      
                    if (resultIndex !== -1) {
                      results[resultIndex].databaseId = insertedData[0].id;
                      results[resultIndex].watermarkedUrl = insertedData[0].export_url_wm;
                      // Store source image info in the client-side results
                      results[resultIndex].sourceImage = sourceImageData;
                    }
                    
                    // Log association between export and source image (for debugging)
                    console.log(`Export ${insertedData[0].id} created from source image:`, sourceImageData);
                  }
                }
              } catch (dbError) {
                console.error("Error saving to exports table:", dbError);
              }
            }
          } catch (templateError) {
            console.error(`Error processing template ${template.name}:`, templateError);
            // Continue to next template
          }
        } // End of templates loop
      } // End of images loop
      
      if (results.length > 0) {
        setGeneratedTemplates(results);
        setProgress(`Successfully generated ${results.length} templates from ${imagesToProcess.length} images!`);
        setCompleted(true);
        setCustomizationStep(false);
        
        // Show success toast
        toast({
          title: "Templates generated!",
          description: `${results.length} templates have been created from ${imagesToProcess.length} approved images.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error('No templates were successfully generated');
      }
    } catch (err) {
      console.error('Error in template generation:', err);
      setError(err.message && err.message.includes("No approved images") ? 
        "No approved images found. Make sure images have been approved before generating templates." : 
        "Failed to generate templates: " + err.message);
      setDebugInfo(JSON.stringify({
        error: err.message,
        stack: err.stack,
        name: err.name,
        context: `Images: ${imagesToProcess?.length || 0}, Templates: ${templates?.length || 0}`
      }, null, 2));
      toast({
        title: "Generation failed",
        description: err.message && err.message.includes("No approved images") ?
          "No approved images found. Please make sure images have been approved." :
          err.message || 'Failed to generate templates',
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to fetch primary image
  const fetchPrimaryImage = async (imageId) => {
    try {
      const { data, error } = await supabase
        .from('images')
        .select('*')
        .eq('id', imageId)
        .single();
        
      if (error) throw error;
      if (!data) throw new Error("No image found with ID: " + imageId);
      
      console.log("Found primary image:", data);
      return data;
    } catch (err) {
      console.error("Failed to fetch primary image:", err);
      throw err;
    }
  };

  // Helper function to fetch approval data
  const fetchApprovalData = async (id) => {
    try {
      console.log("Fetching approval data for ID: " + id);
      
      const { data, error } = await supabase
        .from('image_approvals')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) throw error;
      
      if (!data) {
        throw new Error("No approval data found for ID: " + id);
      }
      
      console.log("Approval data:", data);
      return data;
    } catch (err) {
      console.error("Error fetching approval data:", err);
      throw err;
    }
  };

  // Helper function to fetch user data
  const fetchUserData = async (userId, eventId) => {
    try {
      console.log("Fetching user data for ID:", userId, "Event ID:", eventId);
      
      // Create a minimal user object that we'll use regardless of DB queries
      const baseUserData = {
        id: userId,
        name: "User " + userId.substring(0, 6),
        first_name: "",
        last_name: "",
        email: "",
      };
      
      // Skip app_users query entirely due to infinite recursion policy error
      console.log("Skipping app_users query due to policy issues");
      
      // Get event details - this should work
      try {
        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select('*')
          .eq('id', eventId)
          .limit(1);
          
        if (eventError) {
          console.error("Error fetching event data:", eventError);
          // Continue with minimal event data
        } else if (eventData && eventData.length > 0) {
          console.log("Event data retrieved:", eventData[0]);
          // Combine the data with event
          baseUserData.event = eventData[0];
        } else {
          console.warn("No event found with ID:", eventId);
          // Use minimal event data
          baseUserData.event = { id: eventId };
        }
      } catch (eventFetchError) {
        console.error("Failed to fetch event:", eventFetchError);
        // Use minimal event data
        baseUserData.event = { id: eventId };
      }
      
      // Try to get user details from a safer function if available
      try {
        const { data: userDetails, error: userDetailsError } = await supabase.rpc(
          'get_minimal_user_data',
          { user_id_param: userId }
        );
        
        if (!userDetailsError && userDetails) {
          console.log("Got user details from RPC:", userDetails);
          // Merge user details with base data
          Object.assign(baseUserData, userDetails);
        }
      } catch (userDetailsError) {
        console.warn("Could not fetch user details from RPC:", userDetailsError);
      }
      
      console.log("Using user data:", baseUserData);
      return baseUserData;
    } catch (err) {
      console.error("Failed to fetch user data:", err);
      
      // Last resort: return minimal data to continue the process
      const emergencyData = {
        id: userId,
        name: "User " + userId.substring(0, 6),
        first_name: "",
        last_name: "",
        email: "",
        event: { id: eventId }
      };
      
      console.log("Using emergency fallback data:", emergencyData);
      return emergencyData;
    }
  };

  // Helper function to fetch templates
  const fetchTemplates = async (eventId) => {
    try {
      console.log("Fetching event data for ID:", eventId);
      
      // First get the event to retrieve the templates list
      const { data: eventData, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .limit(1); // Instead of single()
        
      if (eventError) {
        console.error("Error fetching event:", eventError);
        throw eventError;
      }
      
      if (!eventData || eventData.length === 0) {
        console.warn("No event found with ID:", eventId);
        throw new Error("Event not found");
      }
      
      const event = eventData[0];
      console.log("Event data retrieved:", event);
      
      // Parse the templates from the event data
      let templateIds = [];
      try {
        if (event.templates) {
          // Check if it's already an array
          if (Array.isArray(event.templates)) {
            templateIds = event.templates;
          } 
          // Check if it's a JSON string
          else if (typeof event.templates === 'string') {
            try {
              templateIds = JSON.parse(event.templates);
            } catch (jsonError) {
              // Try comma-separated format
              templateIds = event.templates.split(',').map(id => id.trim());
            }
          }
        }
      } catch (parseError) {
        console.error("Error parsing templates from event:", parseError);
        // If we can't parse it as JSON, try to use it as a comma-separated string
        if (typeof event.templates === 'string') {
          templateIds = event.templates.split(',').map(id => id.trim());
        }
      }
      
      if (!templateIds || templateIds.length === 0) {
        console.warn("No template IDs found for event:", eventId);
        throw new Error("No templates associated with this event");
      }
      
      console.log("Template IDs for event:", templateIds);
      
      // Now fetch each template by ID
      const validTemplates = [];
      for (const templateId of templateIds) {
        console.log("Fetching template with ID:", templateId);
        
        const { data: template, error: templateError } = await supabase
          .from('templates')
          .select('*')
          .eq('id', templateId)
          .limit(1); // Instead of single()
          
        if (templateError) {
          console.error("Error fetching template:", templateError);
          continue; // Skip this template but continue with others
        }
        
        if (template && template.length > 0) {
          validTemplates.push(template[0]);
        } else {
          console.warn("Template not found:", templateId);
        }
      }
      
      if (validTemplates.length === 0) {
        console.warn("No valid templates found for event");
        throw new Error("No valid templates found for this event");
      }
      
      console.log("Found " + validTemplates.length + " valid templates");
      return validTemplates;
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      throw err;
    }
  };

  // Helper function to find primary image for a user
  const findPrimaryImageForUser = async (userId) => {
    try {
      console.log("Finding primary image for user:", userId);
      
      // First try to find an upload marked as primary
      const { data: primaryUploads, error: primaryError } = await supabase
        .from('user_uploads')
        .select('*')
        .eq('user_id', userId)
        .eq('is_primary', true)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);
        
      if (!primaryError && primaryUploads?.length > 0) {
        console.log("Found primary image from user_uploads:", primaryUploads[0]);
        // Ensure we return with image_url for consistent access
        return {
          id: primaryUploads[0].id,
          image_url: primaryUploads[0].image_url || primaryUploads[0].file_url,
          file_url: primaryUploads[0].file_url,
          user_id: userId
        };
      }
      
      // If no primary image, try to get the most recent upload
      const { data: uploads, error: uploadsError } = await supabase
        .from('user_uploads')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);
      
      if (uploadsError) {
        console.error("Error fetching user uploads:", uploadsError);
        
        // Try a third approach - check storage for background-removed images
        console.log("Falling back to storage for user:", userId);
        const { data: images, error: storageError } = await supabase.storage
          .from('playmakerpics-images')
          .list(`background_removed_images/${userId}`, {
            limit: 1,
            sortBy: { column: 'created_at', order: 'desc' },
          });
          
        if (storageError) {
          console.error("Error checking storage:", storageError);
          throw new Error("Could not find any images for this user");
        }
        
        if (images?.length > 0) {
          const imageUrl = supabase.storage
            .from('playmakerpics-images')
            .getPublicUrl(`background_removed_images/${userId}/${images[0].name}`)
            .data.publicUrl;
            
          console.log("Found image in storage:", imageUrl);
          
          return {
            id: images[0].id || "storage-" + Date.now(),
            image_url: imageUrl,
            file_url: imageUrl,
            user_id: userId
          };
        }
        
        throw new Error("No images found for this user");
      }
      
      if (uploads?.length > 0) {
        console.log("Found most recent upload:", uploads[0]);
        // Ensure we return with image_url for consistent access
        return {
          id: uploads[0].id,
          image_url: uploads[0].image_url || uploads[0].file_url,
          file_url: uploads[0].file_url,
          user_id: userId
        };
      }
      
      throw new Error("No uploads found for this user");
    } catch (err) {
      console.error("Error finding primary image:", err);
      throw err;
    }
  };

  // Helper function to fetch all approved images for a user
  const fetchApprovedImages = async (userId, approvalId) => {
    try {
      console.log(`Fetching approved images for user: ${userId}, approval: ${approvalId}`);
      
      // Get images that were approved during the approval process
      const { data: approvedUploads, error: uploadsError } = await supabase
        .from('user_uploads')
        .select('*')
        .eq('user_id', userId)
        .eq('approval_id', approvalId)
        .eq('status', 'active')
        .eq('approval_status', 'approved')
        .order('created_at', { ascending: false });
      
      if (uploadsError) {
        console.error("Error fetching approved uploads:", uploadsError);
        throw uploadsError;
      }
      
      if (!approvedUploads || approvedUploads.length === 0) {
        console.warn("No approved uploads found for user:", userId);
        
        // Fallback: try to find any approved images for this user regardless of approval_id
        const { data: anyApprovedUploads, error: anyError } = await supabase
          .from('user_uploads')
          .select('*')
          .eq('user_id', userId)
          .eq('status', 'active')
          .eq('approval_status', 'approved')
          .order('created_at', { ascending: false });
        
        if (anyError) {
          console.error("Error in fallback query:", anyError);
          throw anyError;
        }
        
        if (!anyApprovedUploads || anyApprovedUploads.length === 0) {
          // Last resort: check if there's a primary image set
          console.log("Falling back to primary image check");
          const primaryImage = await findPrimaryImageForUser(userId);
          
          if (primaryImage) {
            console.log("Returning just the primary image as fallback");
            return [primaryImage];
          }
          
          throw new Error("No approved images found for this user");
        }
        
        console.log(`Found ${anyApprovedUploads.length} approved uploads through fallback query`);
        return anyApprovedUploads;
      }
      
      console.log(`Found ${approvedUploads.length} approved uploads for approval ${approvalId}`);
      
      // Format the data consistently
      const formattedImages = approvedUploads.map(upload => ({
        id: upload.id,
        image_url: upload.image_url || upload.file_url,
        file_url: upload.file_url,
        user_id: userId,
        is_primary: upload.is_primary
      }));
      
      return formattedImages;
    } catch (err) {
      console.error("Error fetching approved images:", err);
      throw err;
    }
  };

  // Add keyboard navigation handlers
  const handleKeyNavigation = (e) => {
    if (!isModalOpen) return;

    if (e.key === 'ArrowRight') {
      goToNextTemplate();
    } else if (e.key === 'ArrowLeft') {
      goToPrevTemplate();
    } else if (e.key === 'Escape') {
      setIsModalOpen(false);
    }
  };

  // Add useEffect for keyboard event handling
  useEffect(() => {
    if (isModalOpen) {
      window.addEventListener('keydown', handleKeyNavigation);
    }
    
    return () => {
      window.removeEventListener('keydown', handleKeyNavigation);
    };
  }, [isModalOpen, currentTemplateIndex]);

  // First, let's hide other UI elements but not the placeholder form
  const customizationFormStyle = {
    display: "block" // Changed from "none" to "block" to show the form
  };

  // Handle adding a template to the cart
  const handleAddToCart = (template) => {
    console.log('Adding template to cart:', template);
    
    // Check if template is already in cart
    if (cartItems.some(item => item.id === template.id)) {
      toast({
        title: "Already in cart",
        description: "This template is already in your cart.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Create a template object with all necessary properties
    const templateToAdd = {
      id: template.id,
      name: template.name,
      url: template.url || template.supabaseUrl,
      watermarkedUrl: template.watermarkedUrl,
      databaseId: template.databaseId || null,
      imageUrl: template.imageUrl || null,
      fromImage: template.fromImage || 0
    };
    
    // Add template to cart
    const newCartItems = [...cartItems, templateToAdd];
    setCartItems(newCartItems);
    
    // Determine package based on number of items
    const packageOptions = [
      { id: 'basic', name: 'Basic', price: 30, imageCount: 1, description: '1 digital image' },
      { id: 'premium', name: 'Premium', price: 50, imageCount: 2, description: '2 digital images' },
      { id: 'ultimate', name: 'Ultimate', price: 65, imageCount: 3, description: '3 digital images' },
      { id: 'frame', name: 'Frame Package', price: 100, imageCount: 3, description: '3 digital images + 11" x 17" framed print', includedFrames: 1 }
    ];
    
    let newPackage;
    if (newCartItems.length === 1) newPackage = packageOptions[0];
    else if (newCartItems.length === 2) newPackage = packageOptions[1];
    else if (newCartItems.length === 3) newPackage = packageOptions[2];
    else newPackage = { ...packageOptions[2], price: 65 + ((newCartItems.length - 3) * 20) };
    
    setCartPackage(newPackage);
    
    // Show success message
    toast({
      title: "Added to cart",
      description: `${template.name} added to your cart.`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
    
    // Open cart drawer
    onCartOpen();
  };

  // Handle checkout process
  const handleCheckout = (checkoutData) => {
    console.log('Proceeding to checkout with:', checkoutData);
    
    // Create URL parameters for the checkout page
    const params = new URLSearchParams();
    
    // Add essential parameters
    params.append('items', checkoutData.items.length);
    params.append('package', checkoutData.package.id);
    params.append('total', checkoutData.total);
    
    // Add item IDs as a comma-separated list
    const itemIds = checkoutData.items.map(item => item.id || item.databaseId).filter(Boolean).join(',');
    if (itemIds) {
      params.append('item_ids', itemIds);
    }
    
    // Add timestamp to prevent caching
    params.append('t', Date.now());
    
    // Always use the root checkout URL to avoid sidebar navigation
    const checkoutUrl = `/checkout?${params.toString()}`;
    console.log('Navigating to checkout URL:', checkoutUrl);
    
    // Navigate to the checkout page
    window.location.href = checkoutUrl;
  };

  return (
    <Box width="100%" p={6} bg="white" borderRadius="md" boxShadow="sm">
      {/* Header with PlayMaker Pics Logo */}
      <Center mb={6}>
        <Image 
          src="/playmaker-logo.png" 
          alt="PlayMaker Pics Logo"
          fallback={
            <Box bg="blue.600" p={4} borderRadius="md" color="white" fontWeight="bold" fontSize="2xl">
              PlayMaker Pics
            </Box>
          }
          maxH="60px"
        />
      </Center>
      
      <VStack spacing={4} align="stretch">
        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertTitle>Error!</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {loading && (
          <VStack spacing={4}>
            <HStack>
              <Spinner size="sm" color="blue.500" />
              <Text>{progress}</Text>
            </HStack>
            <Progress hasStripe isAnimated colorScheme="blue" value={progress.includes('%') ? parseInt(progress) : undefined} 
                     isIndeterminate={!progress.includes('%')} width="100%" />
          </VStack>
        )}

        {completed && generatedTemplates && generatedTemplates.length > 0 && (
          <VStack spacing={4}>
            <Alert status="success" borderRadius="md" variant="subtle" colorScheme="green" bg="#8dc63f">
              <AlertIcon />
              <Box>
                <AlertTitle>Success!</AlertTitle>
                <AlertDescription>
                  {generatedTemplates.length} Custom Designs have been created from {approvedImages.length} approved images.
                </AlertDescription>
              </Box>
            </Alert>
            
            <Box width="100%">
              <Text fontWeight="bold" mb={2} color="#ff6b00">Generated Custom Designs:</Text>
              
              {/* Mobile-friendly grid of all templates */}
              <SimpleGrid columns={[2, 2, 3]} spacing={3}>
                {generatedTemplates.map((template, idx) => (
                  <Box 
                    key={idx} 
                    borderRadius="md" 
                    overflow="hidden" 
                    borderWidth="1px" 
                    bg="white"
                    cursor="pointer"
                    onClick={() => {
                      setSelectedImageIndex(template.fromImage);
                      setCurrentTemplateIndex(
                        generatedTemplates
                          .filter(t => t.fromImage === template.fromImage)
                          .findIndex(t => t.id === template.id)
                      );
                      setIsModalOpen(true);
                    }}
                    transition="transform 0.2s"
                    _hover={{ transform: "scale(1.03)", boxShadow: "md" }}
                    position="relative"
                    boxShadow="sm"
                    height={{base: "160px", md: "180px"}}
                    role="group"
                  >
                    <Box position="relative" height="100%">
                      <Image 
                        src={template.url || template.supabaseUrl} 
                        alt={`Generated template ${template.name}`} 
                        width="100%" 
                        height="100%"
                        objectFit="cover"
                        bg="white"
                        fallback={<Spinner />} 
                        onError={(e) => {
                          console.error(`Failed to load image: ${template.url || template.supabaseUrl}`);
                          e.target.onerror = null;
                          e.target.src = `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><rect width='200' height='200' fill='%23FF8C42'/><text x='50%' y='50%' font-size='24' text-anchor='middle' fill='white'>${template.name}</text></svg>`;
                        }}
                      />
                      
                      {/* Removing Hover action overlay with Add to Cart & View buttons */}
                      <Flex
                        position="absolute"
                        top="0"
                        left="0"
                        right="0"
                        bottom="0"
                        bg="rgba(0,0,0,0.5)"
                        opacity="0"
                        _groupHover={{ opacity: 1 }}
                        transition="opacity 0.2s"
                        alignItems="center"
                        justifyContent="center"
                        flexDirection="column"
                        gap={2}
                        p={2}
                        zIndex="10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button 
                          size="sm"
                          colorScheme="blue"
                          leftIcon={<FaEye />}
                          width="90%"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImageIndex(template.fromImage);
                            setCurrentTemplateIndex(
                              generatedTemplates
                                .filter(t => t.fromImage === template.fromImage)
                                .findIndex(t => t.id === template.id)
                            );
                            setIsModalOpen(true);
                          }}
                        >
                          View
                        </Button>
                      </Flex>
                      
                      {/* Transparent overlay to prevent right-click save */}
                      <Box 
                        position="absolute" 
                        top="0" 
                        left="0" 
                        width="100%" 
                        height="100%" 
                        onClick={(e) => e.stopPropagation()}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
            
            {/* Updating Close button to View Images button that redirects to dashboard/designs */}
            <Button 
              onClick={() => window.location.href = '/dashboard/designs'} 
              colorScheme="blue"
            >
              View Images
            </Button>
          </VStack>
        )}
        
        {completed && (!generatedTemplates || generatedTemplates.length === 0) && (
          <Alert status="warning" borderRadius="md">
            <AlertIcon />
            <AlertTitle>No templates generated</AlertTitle>
            <AlertDescription>
              The process completed but no templates were generated. Please try again.
            </AlertDescription>
          </Alert>
        )}
        
        {debugInfo && (
          <Box mt={4} p={3} bg="gray.100" borderRadius="md">
            <Heading size="sm" mb={2}>Debug Information:</Heading>
            <Code display="block" whiteSpace="pre" overflowX="auto" p={2}>
              {debugInfo}
            </Code>
          </Box>
        )}
        
        {/* Modal for viewing templates */}
        <Modal 
          isOpen={isModalOpen} 
          onClose={() => setIsModalOpen(false)}
          size="full"
          motionPreset="slideInBottom"
        >
          <ModalOverlay bg="rgba(0,0,0,0.85)" />
          <ModalContent margin={0} borderRadius={0}>
            <ModalHeader display="flex" justifyContent="space-between" alignItems="center" p={2} bg="blue.700" color="white">
              <HStack>
                <Image 
                  src="/playmaker-logo.png" 
                  alt="PlayMaker Pics Logo"
                  fallback={
                    <Box bg="blue.600" p={2} borderRadius="md" color="white" fontWeight="bold" fontSize="md">
                      PlayMaker Pics
                    </Box>
                  }
                  maxH="30px"
                  mr={2}
                />
                <Text fontSize="sm">
                  {selectedImageIndex !== null && 
                    currentTemplateIndex !== null && 
                    getTemplatesForImage(selectedImageIndex).length > 0 && 
                    `${currentTemplateIndex + 1}/${getTemplatesForImage(selectedImageIndex).length}`
                  }
                </Text>
              </HStack>
              <HStack spacing={2} fontSize="xs" color="whiteAlpha.800">
                <Text display={{base: "none", md: "block"}}>Swipe or use ← → keys to navigate</Text>
                <IconButton
                  icon={<ModalCloseButton position="static" size="sm" />}
                  aria-label="Close"
                  size="sm"
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  onClick={() => setIsModalOpen(false)}
                />
              </HStack>
            </ModalHeader>
            
            <ModalBody p={0} bg="black" position="relative" height="calc(100vh - 120px)" display="flex" alignItems="center" justifyContent="center">
              {selectedImageIndex !== null && getTemplatesForImage(selectedImageIndex)[currentTemplateIndex] && (
                <>
                  {/* Large full-screen template image view */}
                  <Box 
                    position="relative" 
                    width="100%" 
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                  >
                    <Image 
                      src={getTemplatesForImage(selectedImageIndex)[currentTemplateIndex]?.url || 
                          getTemplatesForImage(selectedImageIndex)[currentTemplateIndex]?.supabaseUrl} 
                      alt="Template preview"
                      maxH="100%"
                      maxW="100%"
                      objectFit="contain"
                      fallback={<Spinner color="white" size="xl" />}
                    />
                    
                    {/* Transparent overlay to prevent right-click save */}
                    <Box 
                      position="absolute" 
                      top="0" 
                      left="0" 
                      width="100%" 
                      height="100%"
                      onContextMenu={(e) => e.preventDefault()}
                      zIndex={1}
                    />
                    
                    {/* Touch navigation areas */}
                    <Box 
                      position="absolute" 
                      top="0" 
                      left="0" 
                      width="40%" 
                      height="100%" 
                      cursor="pointer"
                      onClick={goToPrevTemplate}
                      zIndex={3}
                    />
                    <Box 
                      position="absolute" 
                      top="0" 
                      right="0" 
                      width="40%" 
                      height="100%" 
                      cursor="pointer"
                      onClick={goToNextTemplate}
                      zIndex={3}
                    />
                    
                    {/* Left/Right navigation buttons on sides - visible on hover */}
                    <IconButton
                      aria-label="Previous"
                      icon={<ChevronLeftIcon boxSize={8} />}
                      position="absolute"
                      left={2}
                      top="50%"
                      transform="translateY(-50%)"
                      colorScheme="blackAlpha"
                      bg="rgba(0,0,0,0.3)"
                      color="white"
                      size="lg"
                      isRound
                      opacity={0.6}
                      _hover={{ opacity: 1 }}
                      onClick={goToPrevTemplate}
                      zIndex={4}
                    />
                    <IconButton
                      aria-label="Next"
                      icon={<ChevronRightIcon boxSize={8} />}
                      position="absolute"
                      right={2}
                      top="50%"
                      transform="translateY(-50%)"
                      colorScheme="blackAlpha"
                      bg="rgba(0,0,0,0.3)"
                      color="white"
                      size="lg"
                      isRound
                      opacity={0.6}
                      _hover={{ opacity: 1 }}
                      onClick={goToNextTemplate}
                      zIndex={4}
                    />
                  </Box>
                </>
              )}
            </ModalBody>
            
            <ModalFooter p={3} bg="gray.900" borderTopWidth="1px" borderColor="gray.700">
              <HStack width="100%" justify="space-between">
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold" color="white">
                    {getTemplatesForImage(selectedImageIndex)?.[currentTemplateIndex]?.name || "Template"}
                  </Text>
                </VStack>
                
                {/* Removing Add to Cart button */}
              </HStack>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {!loading && customizationStep && primaryImage && (
          <Box p={4} borderWidth="1px" borderRadius="md" bg="white" boxShadow="sm" mb={4} style={customizationFormStyle}>
            <form onSubmit={handleCustomizationSubmit}>
              <VStack spacing={4} align="stretch">
                <Text fontWeight="medium" color="blue.700">We ask for basic details like your name, jersey number, and team to personalize your images. These placeholders let us automatically create stunning, custom-edited photos that look like they were designed just for you—because they were.</Text>
                
                {/* Display the placeholder fields */}
                {allPlaceholders.length > 0 && allPlaceholders.map((placeholder, index) => (
                  <FormControl key={index}>
                    <FormLabel>{placeholder.name}</FormLabel>
                    <Input
                      name={placeholder.name}
                      value={formData[placeholder.name] || ''}
                      onChange={handleInputChange}
                      placeholder={`Enter ${placeholder.name}`}
                    />
                  </FormControl>
                ))}
                
                <Button 
                  type="submit" 
                  colorScheme="blue"
                  isLoading={loading}
                  mt={4}
                >
                  Submit
                </Button>
              </VStack>
            </form>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default AutoTemplateGenerator;